"use client";

import Button, { geistSans } from "@/components/atoms/buttons/Button";
import { Calendar, type DateRange } from "@/components/ui/calendar";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn, convertDate, getCalendarLocale } from "@/lib/utils";
import { createPaymentLocalisedSchema, EventInfoSchema } from "@/lib/validation/payment";
import { doesEventDateRangeIncludeUnavailableDate, isEventDateUnavailable } from "@/lib/event-date-availability";
import { useCheckoutStore } from "@/stores/checkout-store";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Locales } from "@/lib/i18n";
import { useRouter } from "next/navigation";
import { __, getPermalink } from "@/lib/i18n/functions";
import { useEffect, useState } from "react";

const getEventTypeOptions = (locale: Locales) => {
  return [
    { value: __("Anniversary", "en"), label: __("Anniversary", locale) },
    { value: __("Corporate Dinner", "en"), label: __("Corporate Dinner", locale) },
    { value: __("Wedding", "en"), label: __("Wedding", locale) },
    { value: __("Funeral Reception", "en"), label: __("Funeral Reception", locale) },
    { value: __("Picnic", "en"), label: __("Picnic", locale) },
    { value: __("Barbecue", "en"), label: __("Barbecue", locale) },
    { value: __("Fancy Fair", "en"), label: __("Fancy Fair", locale) },
    { value: __("Baptism", "en"), label: __("Baptism", locale) },
    { value: __("Cocktail Party", "en"), label: __("Cocktail Party", locale) },
    {
      value: __("New Year's Eve", "en"),
      label: __("New Year's Eve", "en"),
    },
    { value: __("Other", "en"), label: __("Other", locale) },
  ];
};

type EventInformationFormProps = {
  locale: Locales;
};

const EventInformationForm = ({ locale }: EventInformationFormProps) => {
  const router = useRouter();
  const [isCalenderOpen, setIsCalendarOpen] = useState(false);
  const { eventInfoSchema } = createPaymentLocalisedSchema(locale);
  const setData = useCheckoutStore((state) => state.setData);
  const initialDate = useCheckoutStore((state) => state.date);
  const storeEventType = useCheckoutStore((state) => state.eventType);
  const storeMessage = useCheckoutStore((state) => state.message);
  const events = getEventTypeOptions(locale);
  const form = useForm<EventInfoSchema>({
    resolver: zodResolver(eventInfoSchema),
    defaultValues: {
      eventType: storeEventType || "",
      date: {
        from: initialDate?.from ? new Date(initialDate.from) : undefined,
        to: initialDate?.to ? new Date(initialDate.to) : undefined,
      },
      message: storeMessage || "",
    },
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = form;

  useEffect(() => {
    reset({
      eventType: storeEventType || "",
      date: {
        from: initialDate?.from ? new Date(initialDate.from) : undefined,
        to: initialDate?.to ? new Date(initialDate.to) : undefined,
      },
      message: storeMessage || "",
    });
  }, [storeEventType, initialDate, storeMessage, reset]);

  useEffect(() => {
    if (initialDate?.from || initialDate?.to) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const from = initialDate?.from ? new Date(initialDate.from) : undefined;
      const to = initialDate?.to ? new Date(initialDate.to) : undefined;

      const isInvalid =
        (from && from < today) ||
        (to && to < today) ||
        (from && from.toDateString() === today.toDateString()) ||
        (to && to.toDateString() === today.toDateString()) ||
        doesEventDateRangeIncludeUnavailableDate({ from, to });

      if (isInvalid) {
        reset({
          eventType: storeEventType || "",
          date: undefined,
          message: storeMessage || "",
        });
        setData({
          eventType: storeEventType || "",
          date: undefined,
          message: storeMessage || "",
        });
      }
    }
  }, [initialDate, reset, setData, storeEventType, storeMessage]);

  const onSubmit = async (data: EventInfoSchema) => {
    setData(data);
    router.push(getPermalink(locale, "checkout", "customer-info"));
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className={cn("flex flex-col gap-5 md:gap-6", geistSans.className)}>
        <div className="gap flex flex-col items-center gap-5 md:flex-row md:gap-3 lg:flex-col xl:flex-row">
          <FormField
            control={control}
            name="eventType"
            render={({ field }) => (
              <FormItem className={cn("w-full flex-1 gap-1", errors.date && !errors.eventType && "mb-6")}>
                <div className="flex flex-col gap-2.5">
                  <FormLabel className="font-cabinet-grotesk text-base font-medium md:font-bold">
                    {__("What type of events are you planning?", locale)}
                    <span className="text-pink-primary">*</span>
                  </FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="border-border-light font-cabinet-grotesk aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 data-[placeholder]:text-dark-primary text-dark-primary h-12 bg-white text-sm font-bold placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px] [&>svg]:size-5">
                        <SelectValue placeholder={__("Select a topic", locale)} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="font-cabinet-grotesk">
                      {events.map((event) => (
                        <SelectItem key={event.value} className="cursor-pointer" value={event.value}>
                          {event.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => {
              const dateFromError = errors.date?.from;
              const dateToError = errors.date?.to;
              const hasDateError = dateFromError || dateToError;

              return (
                <FormItem className={cn("h-full w-full flex-1 grow gap-1", errors.eventType && !errors.date && "mb-6")}>
                  <div className="flex h-full flex-col justify-between gap-2.5">
                    <FormLabel className="font-cabinet-grotesk mb-auto text-base font-medium md:font-bold">
                      {__("For what dates?", locale)}
                      <span className="text-pink-primary">*</span>
                    </FormLabel>
                    <Popover open={isCalenderOpen} onOpenChange={setIsCalendarOpen}>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            onClick={() => setIsCalendarOpen(true)}
                            variant="secondary"
                            className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 [&>svg]:text-dark-primary hover:text-dark-primary text-primary-light flex h-12 w-full items-center justify-between gap-2 rounded-sm bg-white px-3 text-sm font-medium hover:bg-[#F4F4F499] focus-visible:ring-[2px]"
                            iconRightName="CalendarIcon"
                          >
                            {field.value?.from && field.value?.to
                              ? `${convertDate(field.value.from, "dd MMM yyyy", locale)} - ${convertDate(field.value.to, "dd MMM yyyy", locale)}`
                              : __("Select dates", locale)}
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-full border-none p-0" align="end">
                        <Calendar
                          onClose={() => setIsCalendarOpen(false)}
                          title={__("Event Dates", locale)}
                          mode="range"
                          setLocale={getCalendarLocale(locale)}
                          selected={field.value}
                          disabled={isEventDateUnavailable}
                          onSelect={(range: DateRange | undefined, selectedDate: Date) => {
                            if (range && doesEventDateRangeIncludeUnavailableDate(range)) {
                              field.onChange({ from: selectedDate, to: selectedDate });
                              return;
                            }

                            field.onChange(range);
                          }}
                          autoFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  {hasDateError && (
                    <div className="text-pink-primary text-sm font-medium">{dateFromError?.message}</div>
                  )}
                </FormItem>
              );
            }}
          />
        </div>
        <FormField
          control={control}
          name="message"
          render={({ field }) => (
            <FormItem className="w-full gap-1">
              <div className="flex flex-col gap-2.5">
                <FormLabel className="mb-0 text-base font-medium">Notes</FormLabel>
                <FormControl>
                  <Textarea
                    className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light min-h-[157px] resize-none bg-white placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                    placeholder={__("Please let us know your instructions to facilitate delivery.", locale)}
                    {...field}
                  />
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          className="h-12 transition-transform duration-150 ease-in-out active:scale-95 min-sm:ml-auto min-sm:h-[60px]"
        >
          {__("Next step", locale)}
        </Button>
      </form>
    </Form>
  );
};

export default EventInformationForm;
